import { BUCKET_INTERVAL } from "@/utils/date/timeBucketing"
import { formatNumber } from "@/utils/formatting/format-number"

import type {
  ChartClickData,
  ChartDataPoint,
  ChartWidgetConfig,
} from "../../types/chart"
import {
  AGGREGATION_TYPE,
  TIME_MODE,
  VISUALIZATION_TYPE,
} from "../../types/core"
import { formatChartDateLabel } from "../../utils/chart/dateFormatting"
import {
  splitKeyToDisplayName,
  type DisplayNameLookups,
} from "../../utils/chart/displayNameUtils"
import { UNGROUPED_SERIES_KEY } from "../../utils/chart/splitKeys"
import { getVisibleSplitKeys } from "../../utils/chart/visibilityUtils"
import {
  buildGroupedBarKeys,
  createTimeSeriesBarClickData,
  createWholeRangeBarClickData,
} from "./visualizations/barChartVisualizationState"
import {
  buildHistogramData,
  createHistogramBinClickData,
} from "./visualizations/histogramVisualizationData"

/** A bucket start as an ISO date or date-time; other labels are bins or totals. */
const ISO_BUCKET_PATTERN = /^\d{4}-\d{2}-\d{2}(T|$)/
const RESERVED_KEYS = new Set(["date", "timestamp", "bucketTime"])
const TOP_LIST_LIMIT = 10

interface FigureCell {
  text: string
  /** Drill-down this cell opens, the same one its mark opens in the chart. */
  click?: ChartClickData
  /** Accessible name of the drill-down button. */
  actionLabel?: string
}

interface ChartWidgetFigureModel {
  summary?: string
  columns: Array<{ key: string; header: string; numeric?: boolean }>
  rows: Array<{ key: string; cells: Record<string, FigureCell | string> }>
}

interface FigureInput {
  config: ChartWidgetConfig
  chartData: ChartDataPoint[]
  splitKeys: string[]
  displayNameLookups: DisplayNameLookups | null
  dateRange?: { start: Date; end: Date } | null
  /** Whether table values should carry drill-down buttons. */
  drillDown: boolean
}

function seriesName(
  key: string,
  config: ChartWidgetConfig,
  lookups: DisplayNameLookups | null,
): string {
  if (key === UNGROUPED_SERIES_KEY) {
    return config.customSeriesNames?.[key] ?? config.title ?? "Value"
  }
  return (
    config.customSeriesNames?.[key] ??
    splitKeyToDisplayName(key, config.groupBy || config.splitBy, lookups)
  )
}

function readValue(point: ChartDataPoint, key: string): number | undefined {
  const value = point[key]
  if (value === undefined || value === null || value === "") {
    return undefined
  }
  const numeric = typeof value === "number" ? value : Number(value)
  return Number.isNaN(numeric) ? undefined : numeric
}

function formatValue(value: number, config: ChartWidgetConfig): string {
  if (config.aggregation === AGGREGATION_TYPE.SUCCESS) {
    return `${(value * 100).toFixed(0)}%`
  }
  return formatNumber(value)
}

/** `value` as a percentage of `total`; any nonzero total divides, as the chart's 100% stacking does. */
function share(value: number, total: number): string {
  return total !== 0 ? `${Math.round((value / total) * 100)}%` : "—"
}

function isTimeBucket(point: ChartDataPoint): boolean {
  return (
    ISO_BUCKET_PATTERN.test(point.bucketTime) &&
    !Number.isNaN(new Date(point.bucketTime).getTime())
  )
}

function formatPointLabel(
  point: ChartDataPoint,
  config: ChartWidgetConfig,
): string {
  if (!isTimeBucket(point)) {
    return point.date
  }
  return formatChartDateLabel(
    new Date(point.bucketTime),
    config.bucketInterval || BUCKET_INTERVAL.FOUR_HOURS,
  )
}

interface Extreme {
  value: number
  series: string
  at: string
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`
}

/** Line, area and over-time bar charts: one row per bucket, one column per series. */
function timeSeriesModel({
  config,
  chartData,
  splitKeys,
  displayNameLookups,
  drillDown,
}: FigureInput): ChartWidgetFigureModel {
  const keys =
    splitKeys.length === 0
      ? [UNGROUPED_SERIES_KEY]
      : getVisibleSplitKeys(splitKeys, config)
  const series = keys.map((key) => ({
    key,
    name: seriesName(key, config, displayNameLookups),
  }))
  // Matches the chart's 100% stacking: an area chart expands whenever it shows
  // percentages, a bar chart only when its series are also stacked. Even a
  // single series is drawn as its share of the bucket.
  const asShare =
    config.showAsPercentage === true &&
    (config.visualizationType === VISUALIZATION_TYPE.AREA ||
      (config.visualizationType === VISUALIZATION_TYPE.BAR &&
        Boolean(config.stacked)))
  // A grouped 100% bar stacks each group to 100% on its own, so a series'
  // share is of its group's total, not the whole bucket's.
  const groupedKeys =
    config.visualizationType === VISUALIZATION_TYPE.BAR
      ? buildGroupedBarKeys(config, keys, (groupId) => groupId)
      : null
  const shareGroupOf = new Map<string, string[]>()
  if (groupedKeys) {
    for (const members of Object.values(groupedKeys.groups)) {
      for (const key of members) {
        shareGroupOf.set(key, members)
      }
    }
  }

  const rows = chartData.map((point, rowIndex) => {
    const pointLabel = formatPointLabel(point, config)
    const sumOf = (memberKeys: string[]) =>
      memberKeys.reduce((sum, key) => sum + (readValue(point, key) ?? 0), 0)
    const rowTotal = sumOf(keys)
    const cells: Record<string, FigureCell | string> = { point: pointLabel }
    series.forEach((entry, index) => {
      const value = readValue(point, entry.key)
      if (value === undefined) {
        cells[`series-${index}`] = "—"
        return
      }
      const raw = formatValue(value, config)
      const denominator = shareGroupOf.has(entry.key)
        ? sumOf(shareGroupOf.get(entry.key) ?? [])
        : rowTotal
      const text = asShare ? `${share(value, denominator)} (${raw})` : raw
      const click =
        drillDown && isTimeBucket(point)
          ? (createTimeSeriesBarClickData(point, entry.key, config) ??
            undefined)
          : undefined
      cells[`series-${index}`] = {
        text,
        click,
        actionLabel: `View runs for ${series.length > 1 ? `${entry.name}, ` : ""}${pointLabel}: ${text}`,
      }
    })
    return { key: `${rowIndex}:${point.bucketTime}`, cells }
  })

  let peak: Extreme | null = null
  let low: Extreme | null = null
  for (const point of chartData) {
    for (const entry of series) {
      const value = readValue(point, entry.key)
      if (value === undefined) {
        continue
      }
      const at = formatPointLabel(point, config)
      if (!peak || value > peak.value) {
        peak = { value, series: entry.name, at }
      }
      if (!low || value < low.value) {
        low = { value, series: entry.name, at }
      }
    }
  }
  const where = (entry: Extreme) =>
    series.length > 1 ? `${entry.series}, ${entry.at}` : entry.at
  const extremes = peak && low ? describeExtremes(peak, low, where, config) : ""
  const count =
    series.length > 1
      ? `${series.length} series across ${plural(chartData.length, "point")}`
      : plural(chartData.length, "point")
  const shareNote = asShare
    ? " The chart stacks each point to 100%; the table shows each series' share with its value in brackets."
    : ""

  return {
    summary: extremes ? `${count}. ${extremes}${shareNote}` : undefined,
    columns: [
      { key: "point", header: "Point" },
      ...series.map((entry, index) => ({
        key: `series-${index}`,
        header: entry.name,
        numeric: true,
      })),
    ],
    rows,
  }
}

function describeExtremes(
  peak: Extreme,
  low: Extreme,
  where: (entry: Extreme) => string,
  config: ChartWidgetConfig,
): string {
  return `Highest is ${formatValue(peak.value, config)} (${where(peak)}); lowest is ${formatValue(low.value, config)} (${where(low)}).`
}

/** Pie, top-list and whole-range bar charts: one total per series, as plotted. */
function totalsModel({
  config,
  chartData,
  splitKeys,
  displayNameLookups,
  dateRange,
  drillDown,
}: FigureInput): ChartWidgetFigureModel {
  const visibleKeys = getVisibleSplitKeys(splitKeys, config)
  const type = config.visualizationType
  const keys =
    visibleKeys.length > 0 || type === VISUALIZATION_TYPE.PIE
      ? visibleKeys
      : Object.keys(chartData[0] ?? {}).filter((key) => !RESERVED_KEYS.has(key))
  // A whole-range bar plots the pre-aggregated first row; the others sum every row.
  const sourceRows =
    type === VISUALIZATION_TYPE.BAR ? chartData.slice(0, 1) : chartData

  let totals = keys
    .map((key) => ({
      key,
      name: seriesName(key, config, displayNameLookups),
      value: sourceRows.reduce(
        (sum, row) => sum + (readValue(row, key) ?? 0),
        0,
      ),
    }))
    .sort((left, right) => right.value - left.value)
  if (type === VISUALIZATION_TYPE.PIE) {
    totals = totals.filter((entry) => entry.value > 0)
  }
  // Shares are of every value, including any the top list leaves out.
  const grandTotal = totals.reduce((sum, entry) => sum + entry.value, 0)
  const valueCount = totals.length
  if (type === VISUALIZATION_TYPE.TOP_LIST) {
    totals = totals.slice(0, TOP_LIST_LIMIT)
  }
  const shown =
    valueCount > totals.length
      ? ` The chart shows the top ${totals.length}.`
      : ""
  const showShare = config.aggregation !== AGGREGATION_TYPE.SUCCESS
  const canDrill =
    drillDown &&
    (type === VISUALIZATION_TYPE.BAR ||
      Boolean(dateRange?.start && dateRange?.end))
  const top = totals[0]

  return {
    summary: top
      ? `${plural(valueCount, "value")}; largest is ${top.name} at ${formatValue(top.value, config)}${showShare ? ` (${share(top.value, grandTotal)})` : ""}.${shown}`
      : undefined,
    columns: [
      { key: "name", header: "Value" },
      { key: "total", header: "Total", numeric: true },
      ...(showShare ? [{ key: "share", header: "Share", numeric: true }] : []),
    ],
    rows: totals.map((entry) => {
      const text = formatValue(entry.value, config)
      const wholeRange = canDrill
        ? createWholeRangeBarClickData(entry, config, dateRange)
        : null
      // A top-list bar labels its drill-down with the widget title, not the category.
      const click =
        wholeRange && type === VISUALIZATION_TYPE.TOP_LIST
          ? { ...wholeRange, displayLabel: config.title || "Metric" }
          : (wholeRange ?? undefined)
      return {
        key: entry.key,
        cells: {
          name: entry.name,
          total: {
            text,
            click,
            actionLabel: `View runs for ${entry.name}: ${text}`,
          },
          share: share(entry.value, grandTotal),
        },
      }
    }),
  }
}

/** Histograms: one row per bin, as plotted, with the bin's drill-down. */
function histogramModel({
  config,
  chartData,
  dateRange,
  drillDown,
}: FigureInput): ChartWidgetFigureModel {
  const { histogramData, iqrFences } = buildHistogramData(chartData)
  const total = histogramData.reduce((sum, bin) => sum + bin.count, 0)
  const mode = histogramData.reduce<(typeof histogramData)[number] | null>(
    (best, bin) => (!best || bin.count > best.count ? bin : best),
    null,
  )
  // Narrow bins need more than two decimals to stay distinct, as the chart's
  // own bin labels do.
  const range = (bin: { min: number; max: number }) => {
    const width = bin.max - bin.min
    const decimals = width > 0 ? Math.max(2, Math.ceil(-Math.log10(width))) : 2
    return `${formatNumber(bin.min, decimals)} – ${formatNumber(bin.max, decimals)}`
  }

  return {
    summary: mode
      ? `${plural(total, "reading")} in ${plural(histogramData.length, "bin")}; most fall in ${range(mode)} (${mode.count}).${iqrFences ? " Outliers are left out of the bins." : ""}`
      : undefined,
    columns: [
      { key: "range", header: "Range" },
      { key: "count", header: "Count", numeric: true },
      { key: "share", header: "Share", numeric: true },
    ],
    rows: histogramData.map((bin, index) => ({
      key: `${index}:${bin.label}`,
      cells: {
        range: range(bin),
        count: {
          text: String(bin.count),
          click: drillDown
            ? createHistogramBinClickData(bin, config, dateRange, iqrFences)
            : undefined,
          actionLabel: `View runs from ${range(bin)}: ${bin.count}`,
        },
        share: share(bin.count, total),
      },
    })),
  }
}

/** Summary and table for a dashboard chart, built from the data the chart plots. */
export function buildChartWidgetFigure(
  input: FigureInput,
): ChartWidgetFigureModel {
  const type = input.config.visualizationType
  if (type === VISUALIZATION_TYPE.HISTOGRAM) {
    return histogramModel(input)
  }
  if (
    type === VISUALIZATION_TYPE.PIE ||
    type === VISUALIZATION_TYPE.TOP_LIST ||
    (type === VISUALIZATION_TYPE.BAR &&
      input.config.timeMode === TIME_MODE.WHOLE_RANGE)
  ) {
    return totalsModel(input)
  }
  return timeSeriesModel(input)
}
